import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Search, X, ChevronDown, Bike, SlidersHorizontal } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import BikeCard from "@/components/site/BikeCard";
import ScrollCue from "@/components/site/ScrollCue";
import CompareTray from "@/components/site/CompareTray";
import PageSeo from "@/components/site/PageSeo";
import { usePageCopy } from "@/contexts/SettingsContext";
import { addToCompare, getCompareIds } from "@/lib/compare";

const SUBS = {
    "Scooty & EV": ["110cc Scooty", "125cc Sport Scooty", "Electric Scooter"],
    "Motorcycle": ["125cc+", "150cc+", "200cc+", "350cc & Above"],
};

const SORTS = [
    { value: "popularity", label: "Popular" },
    { value: "price_asc", label: "Low to high" },
    { value: "price_desc", label: "High to low" },
    { value: "rating", label: "Rated" },
    { value: "newest", label: "Newest" },
];

const BUDGETS = [
    { id: "any", label: "Any budget", min: "", max: "" },
    { id: "u1", label: "Under ₹1L", min: "", max: "100000" },
    { id: "1-2", label: "₹1–2L", min: "100000", max: "200000" },
    { id: "2-3", label: "₹2–3L", min: "200000", max: "300000" },
    { id: "3p", label: "₹3L+", min: "300000", max: "" },
];

const formatINR = (n) => {
    const v = Number(n);
    if (!v) return "";
    return `₹${v.toLocaleString("en-IN")}`;
};

const Segmented = ({ label, value, options, onChange, testId }) => (
    <div data-testid={testId}>
        <p className="text-[10px] font-bold uppercase tracking-[0.16em] text-[#7a8597] mb-1.5">{label}</p>
        <div className="flex flex-wrap gap-1" role="group" aria-label={label}>
            {options.map((opt) => {
                const active = value === opt.value;
                return (
                    <button
                        key={opt.value || "all"}
                        type="button"
                        onClick={() => onChange(opt.value)}
                        aria-pressed={active}
                        className={`h-9 px-3.5 rounded-full text-[13px] font-medium border transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#E31837] focus-visible:ring-offset-1 ${
                            active
                                ? "bg-[#E31837] border-[#E31837] text-white"
                                : "bg-[#F4F6F8] border-transparent text-[#0A2540] hover:bg-[#fff5f5]"
                        }`}
                    >
                        {opt.label}
                    </button>
                );
            })}
        </div>
    </div>
);

const SEARCH_DEBOUNCE_MS = 350;

const BikesPage = () => {
    const [params, setParams] = useSearchParams();
    const [items, setItems] = useState([]);
    const [total, setTotal] = useState(0);
    const [meta, setMeta] = useState(null);
    const [compareIds, setCompareIds] = useState(getCompareIds);
    const [loading, setLoading] = useState(true);
    const [searchInput, setSearchInput] = useState(() => params.get("q") || "");
    const [filtersOpen, setFiltersOpen] = useState(false);

    const limit = 12;

    const filters = useMemo(() => ({
        q: params.get("q") || "",
        category: params.get("category") || "",
        subcategory: params.get("subcategory") || "",
        fuel_type: params.get("fuel_type") || "",
        brand: params.get("brand") || "",
        price_min: params.get("price_min") || "",
        price_max: params.get("price_max") || "",
        sort: params.get("sort") || "popularity",
        is_featured: params.get("is_featured") || "",
        is_upcoming: params.get("is_upcoming") || "",
    }), [params]);

    const page = Math.max(1, parseInt(params.get("page") || "1", 10) || 1);

    // Keep local search box in sync when URL q changes (clear / browser back)
    useEffect(() => {
        setSearchInput(filters.q);
    }, [filters.q]);

    // Debounce search → URL
    useEffect(() => {
        const trimmed = searchInput.trim();
        const current = filters.q;
        if (trimmed === current) return undefined;
        const t = setTimeout(() => {
            const next = new URLSearchParams(params);
            if (trimmed) next.set("q", trimmed);
            else next.delete("q");
            next.delete("page");
            setParams(next, { replace: true });
        }, SEARCH_DEBOUNCE_MS);
        return () => clearTimeout(t);
        // intentionally depend on searchInput only; params/filters read at fire time
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchInput]);

    useEffect(() => {
        const sync = () => setCompareIds(getCompareIds());
        window.addEventListener("compare-updated", sync);
        return () => window.removeEventListener("compare-updated", sync);
    }, []);

    useEffect(() => { api.get("/bikes/meta").then(r => setMeta(r.data)); }, []);

    useEffect(() => {
        const q = new URLSearchParams();
        Object.entries(filters).forEach(([k, v]) => { if (v) q.set(k, v); });
        if (!filters.is_upcoming) q.set("is_upcoming", "false");
        q.set("limit", String(limit));
        q.set("skip", String((page - 1) * limit));
        const ctrl = new AbortController();
        setLoading(true);
        api.get(`/bikes?${q.toString()}`, { signal: ctrl.signal }).then(r => {
            setItems(r.data.items || []);
            setTotal(r.data.total || 0);
            setLoading(false);
        }).catch((err) => {
            if (err?.code === "ERR_CANCELED" || err?.name === "CanceledError") return;
            setItems([]);
            setTotal(0);
            setLoading(false);
        });
        return () => ctrl.abort();
    }, [filters, page]);

    const patchParams = (patch) => {
        const next = new URLSearchParams(params);
        Object.entries(patch).forEach(([k, v]) => {
            if (v == null || v === "") next.delete(k);
            else next.set(k, String(v));
        });
        if (Object.prototype.hasOwnProperty.call(patch, "category")) next.delete("subcategory");
        next.delete("page");
        setParams(next);
    };

    const setFilter = (k, v) => patchParams({ [k]: v });

    const setPage = (p) => {
        const next = new URLSearchParams(params);
        if (p <= 1) next.delete("page");
        else next.set("page", String(p));
        setParams(next);
        window.scrollTo({ top: 0, behavior: "auto" });
    };

    const toggleBrand = (b) => {
        const cur = (filters.brand || "").split(",").filter(Boolean);
        const next = cur.includes(b) ? cur.filter(x => x !== b) : [...cur, b];
        setFilter("brand", next.join(","));
    };

    const clearAll = () => {
        setSearchInput("");
        setParams({});
    };

    const clearSearch = () => {
        setSearchInput("");
        if (!filters.q) return;
        const next = new URLSearchParams(params);
        next.delete("q");
        next.delete("page");
        setParams(next);
    };

    const onCompare = (bike) => {
        const result = addToCompare(bike.id);
        if (result.added) {
            toast.success(`${bike.name} added to compare shortlist`);
        } else if (result.full) {
            toast.error("Shortlist full (3 bikes). Remove one or go to Compare.");
        } else {
            toast.info(`${bike.name} is already in your shortlist`);
        }
    };

    const brands = Object.keys(meta?.brands || {}).sort();
    const subs = filters.category ? (SUBS[filters.category] || []) : [];
    const pageCount = Math.max(1, Math.ceil(total / limit));
    const copy = usePageCopy("bikes", { title: "All Bikes", subtitle: "Filter by budget, brand, and fuel type." });

    const selectedBrands = (filters.brand || "").split(",").filter(Boolean);
    const sortLabel = filters.sort && filters.sort !== "popularity"
        ? SORTS.find((s) => s.value === filters.sort)?.label
        : "";
    const hiddenCount = [
        filters.category, filters.subcategory, filters.fuel_type,
        filters.price_min || filters.price_max, sortLabel,
    ].filter(Boolean).length + selectedBrands.length;
    const hasHiddenFilters = hiddenCount > 0;

    const priceChipLabel = () => {
        const band = BUDGETS.find((b) => b.id !== "any" && b.min === (filters.price_min || "") && b.max === (filters.price_max || ""));
        if (band) return band.label;
        if (filters.price_min && filters.price_max) return `${formatINR(filters.price_min)} – ${formatINR(filters.price_max)}`;
        if (filters.price_min) return `From ${formatINR(filters.price_min)}`;
        return `Up to ${formatINR(filters.price_max)}`;
    };

    return (
        <div data-testid="bikes-page">
            <PageSeo pageKey="bikes" />

            <section className="container-x pt-6 pb-28">
            <div className="flex items-center justify-between gap-4 mb-3">
                <div className="flex items-center gap-3 min-w-0">
                    <span className="shrink-0 h-11 w-11 rounded-2xl bg-[#fff0f1] text-[#E31837] inline-flex items-center justify-center">
                        <Bike size={20} strokeWidth={1.75} />
                    </span>
                    <div className="min-w-0">
                        <h1 className="font-display font-bold text-xl sm:text-2xl text-[#0A2540] leading-tight">{copy.title}</h1>
                        <p className="text-[13px] text-[#7a8597] mt-0.5 line-clamp-1">{copy.subtitle || "Filter by budget, brand, and fuel type."}</p>
                    </div>
                </div>
                <p className="text-sm text-[#7a8597] shrink-0">{loading ? "…" : `${total} ${total === 1 ? "bike" : "bikes"}`} · {compareIds.length} shortlisted</p>
            </div>

            <div className="mb-4 sticky top-[5.25rem] sm:top-[6.25rem] md:top-[7rem] z-30 rounded-2xl border border-[#e8eaee] bg-white px-2 py-2 shadow-[0_8px_24px_rgba(10,37,64,0.05)] lg:static" data-testid="bikes-catalogue-filter">
                <div className="flex items-center gap-1">
                    <div className="flex items-center gap-2 flex-1 min-w-0 h-9 pl-3 pr-1.5 rounded-full bg-[#F4F6F8]">
                        <Search size={14} className="text-[#7a8597] shrink-0" aria-hidden />
                        <input
                            className="bg-transparent w-full min-w-0 text-[13px] text-[#0A2540] placeholder:text-[#98a2b3] outline-none"
                            placeholder="Search bikes or brands"
                            value={searchInput}
                            onChange={(e) => setSearchInput(e.target.value)}
                            data-testid="filter-search"
                        />
                        {searchInput && (
                            <button
                                type="button"
                                onClick={clearSearch}
                                aria-label="Clear search"
                                className="shrink-0 h-6 w-6 inline-flex items-center justify-center rounded-full text-[#7a8597] hover:bg-white hover:text-[#E31837]"
                            >
                                <X size={13} />
                            </button>
                        )}
                    </div>
                    <button
                        type="button"
                        onClick={() => setFiltersOpen((v) => !v)}
                        aria-expanded={filtersOpen}
                        className={`shrink-0 inline-flex items-center gap-1.5 h-9 pl-3 pr-2.5 ml-1 border-l border-[#eef0f3] text-[13px] font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#E31837] ${
                            filtersOpen ? "text-[#E31837]" : "text-[#0A2540] hover:text-[#E31837]"
                        }`}
                    >
                        <SlidersHorizontal size={14} />
                        <span className="sm:hidden">{filtersOpen ? "Hide" : "Filters"}</span>
                        <span className="hidden sm:inline">{filtersOpen ? "Hide" : "More filters"}</span>
                        {hiddenCount > 0 && (
                            <span className="min-w-[1.1rem] text-center text-[10px] font-bold rounded bg-[#E31837] text-white px-1">{hiddenCount}</span>
                        )}
                        <ChevronDown size={14} className={`transition-transform ${filtersOpen ? "rotate-180" : ""}`} />
                    </button>
                </div>

                {hasHiddenFilters && !filtersOpen && (
                    <div className="mt-1.5 flex items-center gap-1">
                    <ScrollCue className="flex items-center gap-1">
                        {selectedBrands.map((b) => (
                            <button key={b} type="button" onClick={() => toggleBrand(b)} className="shrink-0 inline-flex items-center gap-1 h-6 pl-2 pr-1 rounded-md bg-[#F4F6F8] border border-[#e5e7eb] text-[11px] text-[#0A2540]">
                                {b} <X size={11} className="text-[#E31837]" />
                            </button>
                        ))}
                        {sortLabel && (
                            <button type="button" onClick={() => setFilter("sort", "")} className="shrink-0 inline-flex items-center gap-1 h-6 pl-2 pr-1 rounded-md bg-[#F4F6F8] border border-[#e5e7eb] text-[11px] text-[#0A2540]">
                                {sortLabel} <X size={11} className="text-[#E31837]" />
                            </button>
                        )}
                        {filters.category && (
                            <button type="button" onClick={() => setFilter("category", "")} className="shrink-0 inline-flex items-center gap-1 h-6 pl-2 pr-1 rounded-md bg-[#F4F6F8] border border-[#e5e7eb] text-[11px] text-[#0A2540]">
                                {filters.category} <X size={11} className="text-[#E31837]" />
                            </button>
                        )}
                        {filters.fuel_type && (
                            <button type="button" onClick={() => setFilter("fuel_type", "")} className="shrink-0 inline-flex items-center gap-1 h-6 pl-2 pr-1 rounded-md bg-[#F4F6F8] border border-[#e5e7eb] text-[11px] text-[#0A2540]">
                                {filters.fuel_type} <X size={11} className="text-[#E31837]" />
                            </button>
                        )}
                        {filters.subcategory && (
                            <button type="button" onClick={() => setFilter("subcategory", "")} className="shrink-0 inline-flex items-center gap-1 h-6 pl-2 pr-1 rounded-md bg-[#F4F6F8] border border-[#e5e7eb] text-[11px] text-[#0A2540]">
                                {filters.subcategory} <X size={11} className="text-[#E31837]" />
                            </button>
                        )}
                        {(filters.price_min || filters.price_max) && (
                            <button type="button" onClick={() => patchParams({ price_min: "", price_max: "" })} className="shrink-0 inline-flex items-center gap-1 h-6 pl-2 pr-1 rounded-md bg-[#F4F6F8] border border-[#e5e7eb] text-[11px] text-[#0A2540]">
                                {priceChipLabel()}
                                <X size={11} className="text-[#E31837]" />
                            </button>
                        )}
                    </ScrollCue>
                        <button type="button" onClick={clearAll} className="shrink-0 whitespace-nowrap text-[11px] text-[#E31837] font-semibold inline-flex items-center gap-1 h-7 px-2" data-testid="filter-clear">
                            <X size={12} /> Clear
                        </button>
                    </div>
                )}

            {filtersOpen && (
            <div className="mt-2 pt-2 border-t border-[#eef0f3]">
                {(hasHiddenFilters || searchInput.trim()) && (
                    <div className="flex justify-end px-1">
                        <button type="button" onClick={clearAll} className="shrink-0 whitespace-nowrap text-[11px] text-[#E31837] font-semibold inline-flex items-center gap-1 h-7 px-2" data-testid="filter-clear">
                            <X size={12} /> Clear
                        </button>
                    </div>
                )}
            <ScrollCue axis="y" className="px-1 pb-1 space-y-3.5 max-h-[34vh] lg:max-h-none">
                    <div>
                        <p className="text-[10px] font-bold uppercase tracking-[0.16em] text-[#7a8597] mb-1.5">Brands <span className="hidden sm:inline normal-case tracking-normal font-medium text-[#98a2b3]">· tap any to add or remove</span></p>
                        <div className="flex flex-wrap gap-1.5">
                            {brands.map((b) => {
                                const active = (filters.brand || "").split(",").includes(b);
                                return (
                                    <button
                                        key={b}
                                        type="button"
                                        onClick={() => toggleBrand(b)}
                                        aria-pressed={active}
                                        className={`shrink-0 inline-flex items-center gap-1.5 h-9 pl-3.5 pr-1.5 rounded-full text-[13px] font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#E31837] focus-visible:ring-offset-1 ${
                                            active
                                                ? "bg-[#E31837] text-white"
                                                : "bg-[#F4F6F8] text-[#0A2540] hover:bg-[#fff5f5]"
                                        }`}
                                        data-testid={`filter-brand-${b.replace(/\s/g, "")}`}
                                    >
                                        {b}
                                        <span className={`min-w-[1.15rem] text-center text-[10px] font-bold rounded px-1 ${active ? "bg-white/20" : "bg-white text-[#7a8597]"}`}>
                                            {meta?.brands?.[b] || 0}
                                        </span>
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                    <div className="flex flex-col gap-3 md:flex-row md:flex-wrap md:gap-x-8 md:gap-y-3">
                        <Segmented
                            label="Sort"
                            testId="filter-sort"
                            value={filters.sort}
                            onChange={(v) => setFilter("sort", v)}
                            options={SORTS}
                        />
                        <Segmented
                            label="Type"
                            testId="filter-category"
                            value={filters.category}
                            onChange={(v) => setFilter("category", v)}
                            options={[
                                { value: "", label: "All types" },
                                { value: "Scooty & EV", label: "Scooty & EV" },
                                { value: "Motorcycle", label: "Motorcycle" },
                            ]}
                        />
                        <Segmented
                            label="Fuel"
                            testId="filter-fuel"
                            value={filters.fuel_type}
                            onChange={(v) => setFilter("fuel_type", v)}
                            options={[
                                { value: "", label: "Any fuel" },
                                { value: "Petrol", label: "Petrol" },
                                { value: "Electric", label: "Electric" },
                            ]}
                        />
                    </div>

                    {subs.length > 0 && (
                        <Segmented
                            label="Class"
                            testId="filter-subcategory"
                            value={filters.subcategory}
                            onChange={(v) => setFilter("subcategory", v)}
                            options={[{ value: "", label: "All classes" }, ...subs.map((s) => ({ value: s, label: s }))]}
                        />
                    )}

                    <div>
                        <p className="text-[10px] font-bold uppercase tracking-[0.16em] text-[#7a8597] mb-1.5">Budget</p>
                        <div className="flex flex-wrap items-center gap-1.5">
                            <div className="flex flex-wrap gap-1" role="group" aria-label="Budget">
                                {BUDGETS.map((b) => {
                                    const active = (filters.price_min || "") === b.min && (filters.price_max || "") === b.max;
                                    return (
                                        <button
                                            key={b.id}
                                            type="button"
                                            onClick={() => patchParams({ price_min: b.min, price_max: b.max })}
                                            aria-pressed={active}
                                            className={`h-9 px-3.5 rounded-full text-[13px] font-medium border transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#E31837] focus-visible:ring-offset-1 ${
                                                active
                                                    ? "bg-[#E31837] border-[#E31837] text-white"
                                                    : "bg-[#F4F6F8] border-transparent text-[#0A2540] hover:bg-[#fff5f5]"
                                            }`}
                                        >
                                            {b.label}
                                        </button>
                                    );
                                })}
                            </div>
                            <div className="flex w-full sm:w-auto items-center gap-1.5 sm:ml-1">
                                <input type="number" className="h-9 min-w-0 flex-1 sm:flex-none sm:w-[5.5rem] rounded-full bg-[#F4F6F8] px-3 text-[13px] text-[#0A2540] outline-none focus:ring-2 focus:ring-[#E31837]" placeholder="Min ₹" value={filters.price_min} onChange={(e) => setFilter("price_min", e.target.value)} data-testid="filter-price-min" aria-label="Minimum price" />
                                <span className="text-[#98a2b3]">–</span>
                                <input type="number" className="h-9 min-w-0 flex-1 sm:flex-none sm:w-[5.5rem] rounded-full bg-[#F4F6F8] px-3 text-[13px] text-[#0A2540] outline-none focus:ring-2 focus:ring-[#E31837]" placeholder="Max ₹" value={filters.price_max} onChange={(e) => setFilter("price_max", e.target.value)} data-testid="filter-price-max" aria-label="Maximum price" />
                            </div>
                        </div>
                    </div>

            </ScrollCue>
            </div>
            )}
            </div>

            {loading ? (
                <div className="bike-card p-10 text-center text-[#7a7a84]">Loading bikes…</div>
            ) : items.length === 0 ? (
                <div className="bike-card p-10 text-center">
                    <p className="text-[#4a4a52]">No bikes match your filters.</p>
                    <button onClick={clearAll} className="btn btn-primary mt-4">Reset filters</button>
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {items.map(b => (
                        <BikeCard
                            key={b.id}
                            bike={b}
                            onCompare={onCompare}
                            inCompare={compareIds.includes(b.id)}
                        />
                    ))}
                </div>
            )}

            {pageCount > 1 && (
                <div className="flex items-center justify-center gap-2 mt-8" data-testid="pagination">
                    <button onClick={() => setPage(Math.max(1, page - 1))} disabled={page === 1} className="btn btn-white btn-sm disabled:opacity-40">Prev</button>
                    <span className="text-sm text-[#4a4a52]">Page {page} of {pageCount}</span>
                    <button onClick={() => setPage(Math.min(pageCount, page + 1))} disabled={page === pageCount} className="btn btn-white btn-sm disabled:opacity-40">Next</button>
                </div>
            )}

            <CompareTray />
            </section>
        </div>
    );
};

export default BikesPage;
